// The alert query factories of API §9.1–9.3 and the two optimistic helpers of the rules list (T-166; ARCH §9.3).
// Web (T-172 … T-174) and the phone (T-180) build against these, so a rule authored on one appears on the other
// through the same keys and the same invalidation rows (`invalidation.ts`: every rule write invalidates the whole
// `alerts` family, because a rule switched on is evaluated at once and the inbox may have moved).
//
//   const presets = useQuery(queries.alertPresets(accountId));
//   const rules   = useQuery(queries.alertRules(accountId));
//   const preview = useQuery({ ...queries.alertBacktest(accountId, draft), enabled: isBacktestable(draft) });
//   await client.patchRule(accountId, ruleId, { enabled: false });
//   await queryClient.invalidateQueries(invalidation.patchRule(accountId));
//
// **The back-test query is keyed on the DRAFT** (`backtestDraftKey`): every distinct draft is its own cache entry,
// so retyping a value the builder already asked about answers from the cache instantly — REQ-170 AC4's "within
// 500 ms" is the server's promise (the demo server measures it), and a cached answer is 0 ms. **It carries no
// `keepPreviousData`** (T-356, D-873): the builder's labels come off the draft and a kept answer is the PREVIOUS
// rule's figure under the new rule's sentence — the defect REQ-170 AC4 v2.6 names (BUG-191) — so both readers
// refused it (`placeholderData: undefined`, D-861 / D-872) and draw the pending state until the answer to the
// sentence on screen arrives. Debouncing keystrokes is the screen's (a timer or `useDeferredValue`); what this
// module fixes is that two spellings of one draft never ask twice: `name` and `enabled` are not in the key (the
// back-test ignores both, D-239), `entityIds` are sorted, and the optional fields are normalised to `null`.
//
// **Optimistic switch and removal** — the rules list's on/off switch (REQ-170 AC6) and delete paint at once from
// the list the screen already holds; the server's own answer replaces them on invalidation. Nothing invents a
// sentence, a count or a "last fired": those are the server's (D-228), exactly as `staging.ts` never invents a
// consequence.
//
// **The inbox (API §9.4, T-167).** `alertInbox` / `alertHistory` are the two views of one paginated list, keyed on
// the filters (never the cursor — `useInfiniteQuery`'s `pageParam` walks the pages, the log's rule); `alert` is one
// row's detail. The six writes are `ApiClient` methods (`markAlertRead`, `markAllAlertsRead`, `snoozeAlert`,
// `removeSnooze`, `dismissAlert`, `runAlertAction`) with their rows in `invalidation.ts`, and what a screen paints
// in the 120 ms before they answer is the helpers below: a row's STATE and the unread count move at once
// (`optimisticAlertRead` …), because those are facts the screen already holds; acting reuses `optimisticStage`
// on the action's own `stages`, so the tray and the tree get the same placeholder an edit gets and never a
// consequence the server has not written.
//
//   const inbox  = useInfiniteQuery({ ...queries.alertInbox(accountId, filters), initialPageParam: undefined,
//                    queryFn: ({ pageParam }) => client.getAlerts(accountId, { view: 'inbox', ...filters, cursor: pageParam }),
//                    getNextPageParam: (last) => last.meta.page?.nextCursor ?? undefined });
//   queryClient.setQueryData(queries.alert(accountId, id).queryKey, (d) => d && { ...d, data: optimisticAlertRead([d.data], id)[0] });
//   await client.markAlertRead(accountId, id);
//   await queryClient.invalidateQueries(invalidation.markAlertRead(accountId));

import { keepPreviousData } from '@tanstack/query-core';
import type { ApiClient } from '../api/ApiClient';
import type {
  Alert,
  AlertDetail,
  AlertPreset,
  AlertRule,
  AlertRuleInput,
  BacktestResult,
  RecommendedAction,
  RuleOptions,
} from '../types/alerts';
import type { QueryClient } from '@tanstack/query-core';
import type { ChangeSet } from '../types/changes';
import type { ApiResponse } from '../types/primitives';
import type { Session } from '../types/session';
import { define, type QueryBase } from './define';
import { queryKeys, type AlertListFilters } from './keys';
import { optimisticStage, type OptimisticStage, type StageContext } from './staging';

// The draft key lives beside the other key builders (`keys.ts`) and is re-exported here for the builder's use.
export { backtestDraftKey, type BacktestDraftKey } from './keys';
export type { AlertListFilters, AlertListKey } from './keys';

/** A partially built draft — what the builder holds before the owner has picked everything (REQ-170 AC5). */
export type PartialRuleDraft = Partial<AlertRuleInput>;

/** REQ-170 AC5: the back-test can be asked once metric, condition (with its value) and window are chosen. */
export function isBacktestable(draft: PartialRuleDraft): draft is AlertRuleInput {
  if (draft.metric === undefined || draft.window === undefined || draft.scope === undefined) return false; // prettier-ignore
  const c = draft.condition;
  if (c === undefined) return false;
  if (c.kind === 'threshold') return Number.isFinite(c.value);
  if (c.kind === 'change') return Number.isFinite(c.pct) && c.pct > 0;
  if (c.kind === 'vs_baseline') return Number.isFinite(c.pct) && c.pct > 0 && Number.isInteger(c.baselineDays) && c.baselineDays > 0; // prettier-ignore
  return true;
}

/** The §9.1–9.3 factories — spread into `createQueries` so a screen reads `queries.alertRules(id)` like every other.
 *  `base` is what every factory of one `createQueries` shares (`queryBase`): the stale time and the network mode. */
export function createAlertQueries(client: ApiClient, base: QueryBase) {
  return {
    alertPresets: (accountId: string) =>
      define<ApiResponse<AlertPreset[]>, ReturnType<typeof queryKeys.alertPresets>>({
        queryKey: queryKeys.alertPresets(accountId),
        queryFn: () => client.getAlertPresets(accountId),
        ...base,
      }),
    alertRules: (accountId: string) =>
      define<ApiResponse<AlertRule[]>, ReturnType<typeof queryKeys.alertRules>>({
        queryKey: queryKeys.alertRules(accountId),
        queryFn: () => client.getRules(accountId),
        ...base,
      }),
    alertRule: (accountId: string, ruleId: string) =>
      define<ApiResponse<AlertRule>, ReturnType<typeof queryKeys.alertRule>>({
        queryKey: queryKeys.alertRule(accountId, ruleId),
        queryFn: () => client.getRule(accountId, ruleId),
        ...base,
      }),
    alertRuleOptions: (accountId: string) =>
      define<ApiResponse<RuleOptions>, ReturnType<typeof queryKeys.alertRuleOptions>>({
        queryKey: queryKeys.alertRuleOptions(accountId),
        queryFn: () => client.getRuleOptions(accountId),
        ...base,
      }),
    /**
     * Keyed on the draft: the builder asks on every change and a repeated draft answers from the cache (see the
     * header). Deliberately NO `keepPreviousData` (D-873, REQ-170 AC4): the previous draft's answer under the new
     * draft's sentence is the stale figure the requirement forbids — the readers draw their pending state instead.
     */
    alertBacktest: (accountId: string, draft: AlertRuleInput) =>
      define<ApiResponse<BacktestResult>, ReturnType<typeof queryKeys.alertBacktest>>({
        queryKey: queryKeys.alertBacktest(accountId, draft),
        queryFn: () => client.backtestRule(accountId, draft),
        ...base,
      }),
    // -- §9.4 the inbox (T-167): two views of one paginated list, and one row's detail ---------------------------
    /** `view=inbox` — the last 7 days plus anything still true (REQ-173 AC1); `cursor` is the page's, not the key's. */
    alertInbox: (accountId: string, filters: AlertListFilters = {}, cursor?: string) =>
      define<ApiResponse<Alert[]>, ReturnType<typeof queryKeys.alertList>>({
        queryKey: queryKeys.alertList(accountId, 'inbox', filters),
        queryFn: () =>
          client.getAlerts(accountId, { view: 'inbox', ...filters, ...(cursor === undefined ? {} : { cursor }) }), // prettier-ignore
        ...base,
        // a filter change keeps the last list on screen while the next one loads (the log's argument, REQ-173 AC5)
        placeholderData: keepPreviousData,
      }),
    /** `view=history` — everything else (REQ-173 AC5), same filters, same paging. */
    alertHistory: (accountId: string, filters: AlertListFilters = {}, cursor?: string) =>
      define<ApiResponse<Alert[]>, ReturnType<typeof queryKeys.alertList>>({
        queryKey: queryKeys.alertList(accountId, 'history', filters),
        queryFn: () =>
          client.getAlerts(accountId, { view: 'history', ...filters, ...(cursor === undefined ? {} : { cursor }) }), // prettier-ignore
        ...base,
        placeholderData: keepPreviousData,
      }),
    /** One alert with its rule, evidence, every recommended action and the snooze options (REQ-173 AC4). */
    alert: (accountId: string, alertId: string) =>
      define<ApiResponse<AlertDetail>, ReturnType<typeof queryKeys.alert>>({
        queryKey: queryKeys.alert(accountId, alertId),
        queryFn: () => client.getAlert(accountId, alertId),
        ...base,
      }),
  };
}

export type AlertQueries = ReturnType<typeof createAlertQueries>;

const EMPTY: readonly AlertRule[] = [];
const NO_ROWS: readonly Alert[] = [];

/** The rules list with one switch flipped — what the list paints while `PATCH { enabled }` is in flight. */
export function optimisticRuleSwitch(
  rules: readonly AlertRule[] | null | undefined,
  ruleId: string,
  enabled: boolean,
): AlertRule[] {
  return (rules ?? EMPTY).map((rule) => (rule.id === ruleId ? { ...rule, enabled } : rule));
}

/** The rules list without one rule — what the list paints while `DELETE` is in flight (history stays on the server). */
export function optimisticRuleRemoval(
  rules: readonly AlertRule[] | null | undefined,
  ruleId: string,
): AlertRule[] {
  return (rules ?? EMPTY).filter((rule) => rule.id !== ruleId);
}

/**
 * The rules list with one snooze gone — what the list paints while `DELETE …/snoozes/{id}` is in flight (T-174's
 * `withoutSnooze`, lifted here for both builds; T-286). Rules that held no such snooze are the same objects.
 */
export function optimisticSnoozeRemoval(
  rules: readonly AlertRule[] | null | undefined,
  snoozeId: string,
): AlertRule[] {
  return (rules ?? EMPTY).map((rule) =>
    rule.snoozes.some((s) => s.id === snoozeId)
      ? { ...rule, snoozes: rule.snoozes.filter((s) => s.id !== snoozeId) }
      : rule,
  );
}

// ---- the inbox's optimistic helpers (API §9.4, T-167) — a row's state moves at once; nothing else is invented ----

/** One row of a list (or the detail, passed as `[detail]`) with a patch applied; untouched rows are the same objects. */
function patchRow<T extends Alert>(
  rows: readonly T[] | null | undefined,
  alertId: string,
  patch: Pick<Alert, 'state' | 'outcome'>,
): T[] {
  return (rows ?? (NO_ROWS as readonly T[])).map((row) => (row.id === alertId ? { ...row, ...patch } : row)); // prettier-ignore
}

/** `POST …/{id}/read`: a `new` row reads `seen`; any other state is left as the server said (§9.4 as built). */
export function optimisticAlertRead<T extends Alert>(
  rows: readonly T[] | null | undefined,
  alertId: string,
): T[] {
  return (rows ?? (NO_ROWS as readonly T[])).map((row) =>
    row.id === alertId && row.state === 'new' ? { ...row, state: 'seen' } : row,
  );
}

/** `POST …/read-all`: every `new` row reads `seen`. */
export function optimisticAlertsRead<T extends Alert>(rows: readonly T[] | null | undefined): T[] {
  return (rows ?? (NO_ROWS as readonly T[])).map((row) => (row.state === 'new' ? { ...row, state: 'seen' } : row)); // prettier-ignore
}

/** `POST …/{id}/snooze`: the row reads `snoozed` (it stays in the inbox as the snooze's handle, REQ-174 AC5). */
export function optimisticAlertSnoozed<T extends Alert>(
  rows: readonly T[] | null | undefined,
  alertId: string,
): T[] {
  return patchRow(rows, alertId, { state: 'snoozed', outcome: 'snoozed' });
}

/** `POST …/{id}/dismiss`: the row leaves an INBOX page (a history page keeps it, `outcome: dismissed`). */
export function optimisticAlertDismissed<T extends Alert>(
  rows: readonly T[] | null | undefined,
  alertId: string,
  view: 'inbox' | 'history' = 'inbox',
): T[] {
  if (view === 'inbox') return (rows ?? (NO_ROWS as readonly T[])).filter((row) => row.id !== alertId); // prettier-ignore
  return (rows ?? (NO_ROWS as readonly T[])).map(
    (row) =>
    row.id === alertId ? { ...row, outcome: 'dismissed', state: row.state === 'new' ? 'seen' : row.state } : row, // prettier-ignore
  );
}

/** `POST …/{id}/actions/{key}` answered: the row reads `acted` ("Acted on"; "Planned" in View-only is the client's word). */
export function optimisticAlertActed<T extends Alert>(
  rows: readonly T[] | null | undefined,
  alertId: string,
): T[] {
  return patchRow(rows, alertId, { state: 'acted', outcome: 'acted' });
}

/** `meta.unreadCount` (and `AccountInfo.unreadAlerts`) after a helper above: it moves by the `new` rows that left. */
export function optimisticUnreadCount(
  unreadCount: number | null | undefined,
  before: readonly Alert[] | null | undefined,
  after: readonly Alert[] | null | undefined,
): number | null {
  if (unreadCount === null || unreadCount === undefined) return null;
  const newIn = (rows: readonly Alert[] | null | undefined) =>
    (rows ?? NO_ROWS).filter((row) => row.state === 'new').length;
  return Math.max(0, unreadCount - (newIn(before) - newIn(after)));
}

/**
 * Acting is staging (D-013): the tray placeholder is `optimisticStage` over the action's own `stages` — the same
 * markers an inline edit paints (`withStagedMarkers` takes them) — or `null` for a link action, which stages
 * nothing and which the client follows itself. `context.live` is the row's value (`entity.isOn` / `budget.amount`).
 */
export function optimisticAct(
  current: ChangeSet | null | undefined,
  action: Pick<RecommendedAction, 'stages'>,
  context: StageContext,
): OptimisticStage | null {
  return action.stages === null ? null : optimisticStage(current, action.stages, context);
}

// ---- the two cache helpers both alert screens had typed (T-335, D-1225; D-5 of the slice-0.4 review) ----------

/** One page of the alerts list as the cache holds it: the rows and the `meta.unreadCount` the list carries. */
export interface AlertPage {
  data: Alert[];
  meta: { unreadCount?: number | null };
}

/**
 * `setQueryData` over an INFINITE alerts query: every page's rows go through `patch`, and each page's
 * `meta.unreadCount` moves by the `new` rows that left it (`optimisticUnreadCount`); a page the count
 * cannot be read for keeps its `meta`. `undefined` in, `undefined` out — nothing is invented for an
 * empty cache. Generic over the cache shape so `pageParams` (or anything else beside `pages`) survives.
 */
export function mapAlertPages<S extends { pages: AlertPage[] }>(
  current: S | undefined,
  patch: (rows: readonly Alert[]) => Alert[],
): S | undefined {
  if (!current) return current;
  return {
    ...current,
    pages: current.pages.map((page) => {
      const data = patch(page.data);
      const unreadCount = optimisticUnreadCount(page.meta.unreadCount, page.data, data);
      return {
        ...page,
        data,
        meta: unreadCount === null ? page.meta : { ...page.meta, unreadCount },
      };
    }),
  };
}

/**
 * The session with one account's `unreadAlerts` moved by `next` and floored at zero — the badge both shells
 * draw from `AccountInfo.unreadAlerts` (REQ-173 AC2's "at once"). The pure half of `patchSessionUnread`.
 */
export function withUnreadAlerts(
  current: ApiResponse<Session> | undefined,
  accountId: string,
  next: (current: number) => number,
): ApiResponse<Session> | undefined {
  if (!current) return current;
  return {
    ...current,
    data: {
      ...current.data,
      accounts: current.data.accounts.map((a) =>
        a.id === accountId ? { ...a, unreadAlerts: Math.max(0, next(a.unreadAlerts)) } : a,
      ),
    },
  };
}

/**
 * The badge moves on the session's own cache entry while a read / read-all write is in flight; the refetch
 * afterwards is `invalidation`'s, which names `session` beside `accountInfo` (D-406).
 */
export function patchSessionUnread(
  queryClient: QueryClient,
  key: readonly unknown[],
  accountId: string,
  next: (current: number) => number,
): void {
  queryClient.setQueryData(key, (current: ApiResponse<Session> | undefined) =>
    withUnreadAlerts(current, accountId, next),
  );
}
